import React, { useMemo, type FC } from 'react';
import { StyleSheet, type ViewStyle } from 'react-native';

import { AlignSelf, Stack } from '@/components/layout';
import { StackLayoutProps } from '@/components/layout/stack/types';
import { Text } from '@/components/ui/text';
import { useScaling } from '@/hooks';
import { colors, ColorToken } from '@/theme/colors';

type LevelProps = {
  /** Numeric level to display, e.g. 9 -> "LEVEL 9" */
  value: number;
  alignSelf?: AlignSelf;
  style?: ViewStyle | ViewStyle[];
} & StackLayoutProps;

/**
 * Small LEVEL badge, styled according to the Figma component.
 */
export const Level: FC<LevelProps> = ({ value, style, alignSelf, ...props }) => {
  const { scale } = useScaling();
  const levelColor = useMemo(() => {
    if (value) return colors[`Level ${value}` as ColorToken];
    return colors['Level 9'];
  }, [value]);

  return (
    <Stack
      minWidth={scale(54)}
      paddingX={scale(7)}
      paddingY={scale(2)}
      alignSelf={alignSelf}
      borderWidth={1}
      borderColor={levelColor}
      align="center"
      justify="center"
      style={style}
      {...props}>
      <Text
        variant="Font Inter/Subtitle/Regular"
        style={{ color: levelColor, ...styles.label }}
        numberOfLines={1}
        ellipsizeMode="clip">
        {`LEVEL ${value}`}
      </Text>
    </Stack>
  );
};

const styles = StyleSheet.create({
  label: {
    textAlign: 'center',
  },
});

export default Level;
